import React, { useEffect, useState } from 'react';
import { PaperCard } from '@zola/zola-ui/src/components/PaperCard';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import type { WCardSuiteSearchRequest, WCardSuiteSummaryView } from '@zola/svc-web-api-ts-client';

import { useAppDispatch, useAppSelector } from '@/store';
import { trackCtaClicked, trackProductClicked } from '../../../../../util/trackingHelper';
import { requestPaper } from '../../../../../actions/ShopActions';
import { getShopPaperSuites } from '../../../../../selectors/shopSelectors';
import { Facets } from './types';
import { CARD_SLUGS } from './constants';

import styles from './styles.module.less';

const EXPLORE_ALL_CTA = 'Explore all in Invites + Paper';

const getPLPLink = (facets: Facets): string => {
  const typeSlug = CARD_SLUGS[facets.lead_card_type];
  const motifsAndColors = [...(facets.motifs || []), ...(facets.color || [])];
  const motifsQuery = motifsAndColors.join('--').toLowerCase();

  let url = `/wedding-planning/${typeSlug}/shop`;

  if (motifsQuery) {
    url += `/${motifsQuery}`;
  }

  if (facets.has_magnet) {
    url += '?features=magnet';
  }

  return url;
};

const getPDPLink = (cardType: string, uuid: string): string => {
  const typeSlug = CARD_SLUGS[cardType];
  return `/wedding-planning/${typeSlug}/${uuid}`;
};

type CardSuiteProps = {
  suite: WCardSuiteSummaryView;
  className?: string;
  searchQuery: string;
  colorFacet: string | null;
};

const CardSuite = ({
  suite,
  className,
  searchQuery,
  colorFacet,
}: CardSuiteProps): JSX.Element | null => {
  const { uuid, name, lead_card } = suite;
  if (!uuid || !name || !lead_card) return null;

  const { foil, colors, color_recommendations, type, min_price_cents = 0 } = lead_card;
  if (!type || !colors || colors.length === 0) return null;

  // Try to choose the image corresponding to the current facet color
  let imageURL: string | undefined;
  if (color_recommendations && colorFacet) {
    const recommendedColorCode = color_recommendations[colorFacet];
    const desiredColor = colors.find((color) => color.value === recommendedColorCode);
    imageURL = desiredColor?.plp_front_image_url;
  }

  // If no color is specified or the desired color doesn't have a plp_front_image_url, simply use the 1st available image
  if (!imageURL) {
    imageURL = colors?.find((color) => !!color.plp_front_image_url)?.plp_front_image_url;
  }

  if (!imageURL) return null;

  const [prettyType, prettyName] = name.split('-').map((str) => str.trim());
  const pdpURL = getPDPLink(type, uuid);

  const handleClick = (e?: React.MouseEvent) => {
    // Prevent the default link click (to be sure the analytics call can complete)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    trackProductClicked({
      product_id: uuid,
      sku: lead_card.uuid,
      category: 'Paper',
      name,
      // brand, omitting since does not apply
      // position, omitting since does not apply
      variant: lead_card.uuid,
      price: (min_price_cents / 100).toFixed(2),
      quantity: 1,
      url: pdpURL,
      image_url: imageURL,
      location: 'SEARCH',
      section: 'PAPER_SEARCH_RESULTS',
      search_term: searchQuery,
    });

    setTimeout(() => {
      window.location.href = pdpURL;
    }, 700);
  };

  return (
    <PaperCard
      className={className}
      imageURL={imageURL}
      title={prettyName}
      subtitle={prettyType}
      isFoil={!!foil}
      pathname={pdpURL}
      handleClick={handleClick}
    />
  );
};

interface PaperSearchResultsProps {
  searchQuery: string;
  facets: Facets;
  updateStickySidebar: () => void;
}

const PaperSearchResults = ({
  searchQuery,
  facets,
  updateStickySidebar,
}: PaperSearchResultsProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const cardSuites = useAppSelector(getShopPaperSuites);
  const [plpURL, setPlpURL] = useState('');

  useEffect(() => {
    setPlpURL(getPLPLink(facets));

    const requestBody: WCardSuiteSearchRequest = {
      active_only: true,
      family: undefined,
      has_custom_photo: undefined,
      limit: 4,
      offset: 0,
      orientation: undefined,
      third_party: undefined,
      ...facets,
      lead_card_type: facets.lead_card_type as unknown as WCardSuiteSearchRequest.LeadCardTypeEnum,
    };

    dispatch(requestPaper(requestBody)).finally(() => {
      // Force the sticky sidebar to update its position since album results may have pushed it down the page.
      updateStickySidebar();
    });
  }, [facets, updateStickySidebar, dispatch]);

  // If there are no suites to show, show nothing.
  if (!cardSuites || cardSuites.length < 1) return null;

  const handlePLPLinkClick = (e?: React.MouseEvent) => {
    // Prevent the default link click (to be sure the analytics call can complete)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    trackCtaClicked({
      business_unit: 'INVITATIONS',
      cta_type: 'LINK',
      location: 'SEARCH',
      section: 'PAPER_SEARCH_RESULTS',
      cta: EXPLORE_ALL_CTA,
    });

    setTimeout(() => {
      window.location.href = plpURL;
    }, 700);
  };

  const colorFacet = (facets?.color && facets.color[0]) || null;

  return (
    <div className={styles.paperSearch}>
      <div className={styles.paperSearchContent}>
        <div className={styles.paperSearchHeader}>
          <div className={styles.title}>
            Matches for &quot;<span className={styles.searchQuery}>{searchQuery}</span>&quot; in
            Invites + Paper
          </div>
          <LinkV2 className={styles.link} href={plpURL} arrow onClick={handlePLPLinkClick}>
            {EXPLORE_ALL_CTA}
          </LinkV2>
        </div>
        <div className={styles.paperSearchResults}>
          {cardSuites.map((suite: WCardSuiteSummaryView) => (
            <CardSuite
              className={styles.cardSuite}
              suite={suite}
              key={suite.uuid}
              searchQuery={searchQuery}
              colorFacet={colorFacet}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaperSearchResults;
